import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  ProcessingOverlay,
  type OverlayStatus,
} from "@/components/ProcessingOverlay";
import { parseCostFromResultData } from "@/lib/cost";
import {
  buildGenerateCommand,
  getEngineMode,
  setEngineMode,
  type EngineMode,
} from "@/lib/engine";
import { CommandBlock } from "@/components/CommandBlock";
import type { JobResultCost, LogMessage } from "@/types";

type Tab = "plaud" | "audio" | "manual";

export default function NewMeetingPage() {
  const navigate = useNavigate();

  // 整理引擎：Claude Code（月費）或 API（按次計費）
  const [engineMode, setMode] = useState<EngineMode>(() => getEngineMode());
  const [cliCommand, setCliCommand] = useState<string | null>(null);
  const [cliMeetingId, setCliMeetingId] = useState<string | null>(null);

  function changeMode(next: EngineMode) {
    setMode(next);
    setEngineMode(next);
  }

  // 基本資訊
  const [title, setTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [meetingDate, setMeetingDate] = useState(
    new Date().toISOString().slice(0, 10),
  );

  // 來源
  const [tab, setTab] = useState<Tab>("manual");
  const [plaudUrl, setPlaudUrl] = useState("");
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [manualNotes, setManualNotes] = useState("");

  // References（業主資料）
  const [refs, setRefs] = useState<RefDraft[]>([]);

  // 流程狀態
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // 處理中遮罩
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayStatus, setOverlayStatus] = useState<OverlayStatus>("running");
  const [overlayProgress, setOverlayProgress] = useState<number | null>(null);
  const [overlayStep, setOverlayStep] = useState<string>("準備開始⋯⋯");
  const [overlayLogs, setOverlayLogs] = useState<LogMessage[]>([]);
  const [overlayCost, setOverlayCost] = useState<JobResultCost | null>(null);
  const [overlayError, setOverlayError] = useState<string | null>(null);
  const completedMeetingIdRef = useRef<string | null>(null);

  const titleOk = title.trim().length > 0;
  const sourceOk =
    (tab === "plaud" && plaudUrl.includes("plaud.ai")) ||
    (tab === "audio" && audioFile && audioFile.size <= 25 * 1024 * 1024) ||
    (tab === "manual" && manualNotes.trim().length >= 10);
  const canSubmit = titleOk && !!sourceOk;

  function pushLog(message: string, level: LogMessage["level"] = "info") {
    setOverlayLogs((prev) => [
      ...prev,
      {
        timestamp: Math.floor(Date.now() / 1000),
        level,
        message,
      },
    ]);
  }

  function resetOverlay() {
    setOverlayOpen(false);
    setOverlayStatus("running");
    setOverlayProgress(null);
    setOverlayStep("準備開始⋯⋯");
    setOverlayLogs([]);
    setOverlayCost(null);
    setOverlayError(null);
    completedMeetingIdRef.current = null;
  }

  /**
   * 建會議 + 灌來源 + 上傳業主資料。兩種模式共用的前半段。
   * trace 給 API 模式的處理遮罩用（Claude Code 模式不需要逐步 log）。
   */
  async function prepareMeeting(
    scrapePlaudOnServer: boolean,
    trace?: { step: (s: string) => void; log: (m: string, level?: LogMessage["level"]) => void },
  ) {
    trace?.log("建立會議⋯⋯");
    const { meeting } = await api.createMeeting({
      title: title.trim(),
      client_name: clientName.trim() || undefined,
      meeting_date: meetingDate || undefined,
    });
    trace?.log(`會議建立完成 (${meeting.id})`, "success");

    trace?.step("注入會議來源⋯⋯");
    if (tab === "plaud") {
      trace?.log("送出 PLAUD 連結");
      await api.setPlaudSource(meeting.id, {
        url: plaudUrl.trim(),
        scrape: scrapePlaudOnServer,
      });
    } else if (tab === "audio" && audioFile) {
      trace?.log(
        `上傳音檔 ${audioFile.name} (${(audioFile.size / 1024 / 1024).toFixed(1)} MB)`,
      );
      const form = new FormData();
      form.append("file", audioFile);
      const res = await fetch(`/api/meetings/${meeting.id}/source/audio`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error || "音檔上傳失敗");
      }
      trace?.log("音檔上傳完成", "success");
    } else if (tab === "manual") {
      trace?.log(`手寫筆記 ${manualNotes.trim().length} 字`);
      await api.setManualSource(meeting.id, { notes: manualNotes.trim() });
    }

    for (const r of refs) {
      const refLabel = r.title || r.url || "（未命名）";
      trace?.step(`上傳業主資料：${refLabel}`);
      trace?.log(`業主資料：${refLabel}`);
      await uploadReference(meeting.id, r);
    }

    return meeting;
  }

  /** Claude Code 模式：備好料就停，產生指令讓使用者貼到終端機 */
  async function handleSubmitCli() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const meeting = await prepareMeeting(false);
      await api.markAwaitingCli(meeting.id);
      setCliMeetingId(meeting.id);
      setCliCommand(
        buildGenerateCommand({
          meetingId: meeting.id,
          title: title.trim(),
          clientName: clientName.trim() || null,
          meetingDate: meetingDate || null,
          sourceType: tab,
          plaudUrl: tab === "plaud" ? plaudUrl.trim() : null,
          refCount: refs.length,
        }),
      );
    } catch (e) {
      setErrorMsg((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit() {
    if (!canSubmit || submitting) return;
    if (engineMode === "claude-code") {
      await handleSubmitCli();
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);

    // 開遮罩
    setOverlayOpen(true);
    setOverlayStatus("running");
    setOverlayProgress(null);
    setOverlayStep("建立會議⋯⋯");
    setOverlayLogs([]);
    setOverlayCost(null);
    setOverlayError(null);

    let stopPolling = false;
    let pollPromise: Promise<void> | null = null;

    try {
      const meeting = await prepareMeeting(true, {
        step: setOverlayStep,
        log: pushLog,
      });

      setOverlayStep("Claude 正在整理（會等 15–60 秒，請稍候）⋯⋯");
      pushLog("呼叫 Claude 開始整理");

      // 並行 polling 真實 job 進度
      pollPromise = (async () => {
        while (!stopPolling) {
          try {
            const { job } = await api.getActiveJob(meeting.id);
            if (job) {
              setOverlayProgress(job.progress_percent);
              if (job.step_label) setOverlayStep(job.step_label);
              if (job.log_messages) {
                try {
                  const parsed = JSON.parse(job.log_messages) as LogMessage[];
                  setOverlayLogs(parsed);
                } catch {
                  // 忽略解析錯誤
                }
              }
              if (job.status === "completed" || job.status === "failed") {
                break;
              }
            }
          } catch {
            // 短暫網路錯誤忽略，下回再試
          }
          await new Promise((r) => setTimeout(r, 1200));
        }
      })();

      const { job_id, meeting_id } = await api.startProcessing(meeting.id);
      stopPolling = true;
      if (pollPromise) await pollPromise;

      // 取最終 job（含 result_data 內的成本）
      const { job } = await api.getJob(job_id);
      if (job.log_messages) {
        try {
          setOverlayLogs(JSON.parse(job.log_messages) as LogMessage[]);
        } catch {
          // 忽略
        }
      }

      if (job.status === "failed") {
        setOverlayStatus("failed");
        setOverlayError(job.error_message ?? "處理失敗");
        setOverlayStep("處理失敗");
        return;
      }

      const cost = parseCostFromResultData(job.result_data);
      setOverlayCost(cost);
      setOverlayProgress(100);
      setOverlayStep("完成");
      setOverlayStatus("completed");
      completedMeetingIdRef.current = meeting_id ?? meeting.id;
    } catch (e) {
      stopPolling = true;
      const msg = (e as Error).message;
      setErrorMsg(msg);
      setOverlayStatus("failed");
      setOverlayError(msg);
      setOverlayStep("處理失敗");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 pb-32">
      <h1 className="font-serif text-3xl font-bold text-brand-dark mb-2">
        新增會議
      </h1>
      <p className="text-sm text-brand-dark/60 mb-10 leading-relaxed">
        三步驟把雜亂的會議資料變成可以給業主看的網頁紀錄。
      </p>

      {/* Step 01：基本資訊 */}
      <Step
        num="01"
        title="基本資訊"
        subtitle="會議要叫什麼名字、跟誰開的、什麼時候開的"
        done={titleOk}
      >
        <Field label="會議主題" required>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="例：品牌形象短片｜需求對焦會議"
            className="input"
          />
          <p className="text-xs text-brand-dark/40 mt-1.5">
            等 Claude 整理完，如果它看出更精準的命名會幫你建議
          </p>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="業主">
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="例：客戶名稱或公司"
              className="input"
            />
          </Field>
          <Field label="會議日期">
            <input
              type="date"
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              className="input num"
            />
          </Field>
        </div>
      </Step>

      {/* Step 02：會議來源 */}
      <Step
        num="02"
        title="會議來源"
        subtitle="選一種——PLAUD 連結最快、音檔最完整、手動 key-in 最自由"
        done={!!sourceOk}
      >
        <div className="grid grid-cols-3 gap-2 mb-6">
          <SourceTab
            active={tab === "manual"}
            onClick={() => setTab("manual")}
            label="手動 key-in"
            hint="自己打重點"
          />
          <SourceTab
            active={tab === "plaud"}
            onClick={() => setTab("plaud")}
            label="PLAUD 連結"
            hint="貼分享網址"
          />
          <SourceTab
            active={tab === "audio"}
            onClick={() => setTab("audio")}
            label="音檔上傳"
            hint="MP3 / WAV"
          />
        </div>

        {tab === "manual" && (
          <Field
            label="把這次會議的重點打在這裡"
            hint="越具體越好，金額、日期、人名、決議都寫上"
          >
            <textarea
              value={manualNotes}
              onChange={(e) => setManualNotes(e.target.value)}
              rows={10}
              placeholder={
                "例：\n1. 確認拍攝日期與備案日期\n2. 預算範圍與付款條件\n3. 交付時程與交付物清單"
              }
              className="input font-mono text-sm leading-relaxed"
            />
            <div className="text-xs mt-2 num flex justify-between">
              <span className="text-brand-dark/40">
                {manualNotes.length} 字
              </span>
              <span
                className={
                  manualNotes.trim().length >= 10
                    ? "text-brand-green"
                    : "text-brand-dark/30"
                }
              >
                {manualNotes.trim().length >= 10
                  ? "夠了，可以開始整理"
                  : "至少 10 個字"}
              </span>
            </div>
          </Field>
        )}

        {tab === "plaud" && (
          <Field
            label="PLAUD 公開分享連結"
            hint="從 PLAUD 網頁端「分享」→「公開連結」複製過來"
          >
            <input
              type="url"
              value={plaudUrl}
              onChange={(e) => setPlaudUrl(e.target.value)}
              placeholder="https://web.plaud.ai/s/pub_xxx"
              className="input"
            />
          </Field>
        )}

        {tab === "audio" && (
          <Field
            label="音檔（MP3 / WAV / M4A / AAC / FLAC，限 25MB）"
            hint="超過 25MB 可用 ffmpeg 壓縮：ffmpeg -i in.mp3 -b:a 64k out.mp3"
          >
            <input
              type="file"
              accept="audio/*"
              onChange={(e) => setAudioFile(e.target.files?.[0] ?? null)}
              className="input"
            />
            {audioFile && (
              <div className="text-xs text-brand-dark/60 mt-2 num">
                {audioFile.name} (
                {(audioFile.size / 1024 / 1024).toFixed(1)} MB)
                {audioFile.size > 25 * 1024 * 1024 && (
                  <span className="text-red-700 ml-2">超過 25MB 限制！</span>
                )}
              </div>
            )}
          </Field>
        )}
      </Step>

      {/* Step 03：業主資料 */}
      <Step
        num="03"
        title="業主提供的參考資料"
        subtitle="選填——網頁、檔案、圖片、純文字都可以，可以加多筆。整理完後仍可繼續編輯"
      >
        <ReferenceList refs={refs} onChange={setRefs} />
      </Step>

      {/* Step 04：整理方式 */}
      <Step
        num="04"
        title="要誰來整理"
        subtitle="兩條路產出的東西一樣，差在誰付這筆錢"
        done
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <EngineCard
            active={engineMode === "claude-code"}
            onClick={() => changeMode("claude-code")}
            label="Claude Code"
            price="月費內，單次不計費"
            desc="網頁把資料備好、產生一段指令，你複製到終端機貼上，整理完自動寫回這裡。"
          />
          <EngineCard
            active={engineMode === "api"}
            onClick={() => changeMode("api")}
            label="線上直接整理"
            price="每場約 NT$3"
            desc="網頁直接呼叫 Anthropic API，按下去等 15–60 秒就好。沒有 Claude Code 的人用這條。"
          />
        </div>
      </Step>

      {errorMsg && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
          {errorMsg}
        </div>
      )}

      {/* Sticky 提交列：手機 col stack 避免按鈕被切，桌機維持單列 */}
      <div
        className="fixed bottom-0 left-0 right-0 border-t border-brand-gray/15 bg-white/95 backdrop-blur z-30"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <div className="flex-1 min-w-0 text-xs text-brand-dark/60 leading-tight">
            {!titleOk ? (
              <span>請先填會議主題</span>
            ) : !sourceOk ? (
              <span>請選一種會議來源並填好</span>
            ) : engineMode === "claude-code" ? (
              <span>準備好了，按下去會產生一段指令給你複製</span>
            ) : (
              <span>準備好了，按下「開始整理」即可</span>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 sm:gap-4 flex-shrink-0">
            <button
              onClick={() => navigate("/")}
              disabled={submitting}
              className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm text-brand-dark/60 hover:text-brand-dark"
            >
              取消
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className={cn(
                "px-5 sm:px-6 py-2 sm:py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap",
                canSubmit && !submitting
                  ? "bg-brand-dark text-brand-cream hover:bg-brand-gold"
                  : "bg-brand-gray/30 text-brand-gray cursor-not-allowed",
              )}
            >
              {submitting
                ? "處理中⋯⋯"
                : engineMode === "claude-code"
                  ? "建立並產生指令"
                  : "開始整理"}
            </button>
          </div>
        </div>
      </div>

      {/* Claude Code 模式：資料備好後的指令面板 */}
      {cliCommand && (
        <div className="fixed inset-0 z-50 bg-brand-dark/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto">
          <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl p-5 sm:p-8 min-w-0 max-h-[92vh] overflow-y-auto">
            <div className="text-[11px] font-bold tracking-[0.2em] text-brand-green uppercase mb-2">
              資料已備好
            </div>
            <h2 className="font-serif text-xl sm:text-2xl font-bold text-brand-dark mb-2">
              把這段貼到 Claude Code 終端機
            </h2>
            <p className="text-sm text-brand-dark/60 leading-relaxed mb-5">
              會議已建立，來源與業主資料都上傳好了。整理完會自動寫回這裡，回來重整就看得到。
            </p>

            <CommandBlock
              command={cliCommand}
              hint="Claude 會自己去抓 PLAUD 內容、整理成六章節，再回寫到這場會議。中途不需要你再貼任何東西。"
            />

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-6">
              <button
                onClick={() => navigate(`/meeting/${cliMeetingId}`)}
                className="flex-1 px-5 py-2.5 rounded-full text-sm font-medium bg-brand-dark text-brand-cream hover:bg-brand-gold transition-colors"
              >
                去這場會議的頁面
              </button>
              <button
                onClick={() => navigate("/")}
                className="px-5 py-2.5 rounded-full text-sm text-brand-dark/60 hover:text-brand-dark border border-brand-gray/30"
              >
                回列表
              </button>
            </div>
          </div>
        </div>
      )}

      <ProcessingOverlay
        open={overlayOpen}
        status={overlayStatus}
        progress={overlayProgress}
        stepLabel={overlayStep}
        logs={overlayLogs}
        cost={overlayCost}
        errorMessage={overlayError}
        primaryAction={
          overlayStatus === "completed" && completedMeetingIdRef.current
            ? {
                label: "查看會議紀錄",
                onClick: () => {
                  const target = completedMeetingIdRef.current!;
                  resetOverlay();
                  navigate(`/meeting/${target}`);
                },
              }
            : overlayStatus === "failed"
              ? {
                  label: "回到表單",
                  onClick: resetOverlay,
                }
              : undefined
        }
      />

      <InputStyles />
    </div>
  );
}

function InputStyles() {
  return (
    <style>{`
      input[type="text"], input[type="url"], input[type="date"], input[type="file"], textarea, select {
        width: 100%;
        padding: 10px 16px;
        border: 1px solid rgba(155, 155, 155, 0.3);
        border-radius: 6px;
        background: white;
        color: #2C2C2C;
        font-size: 14px;
        outline: none;
        transition: border-color 0.15s;
        font-family: inherit;
      }
      input:focus, textarea:focus, select:focus {
        border-color: #BF9F5A;
      }
      textarea {
        resize: vertical;
        min-height: 120px;
      }
    `}</style>
  );
}

function Step({
  num,
  title,
  subtitle,
  done,
  children,
}: {
  num: string;
  title: string;
  subtitle?: string;
  done?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-12">
      <div className="flex items-baseline gap-3 mb-1">
        <span
          className={cn(
            "num text-xs font-bold tracking-[0.2em]",
            done ? "text-brand-green" : "text-brand-gold",
          )}
        >
          STEP {num}
        </span>
        <h2 className="font-serif text-xl font-bold text-brand-dark">
          {title}
        </h2>
        {done && (
          <span className="text-xs text-brand-green flex items-center gap-1">
            <svg
              className="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            完成
          </span>
        )}
      </div>
      {subtitle && (
        <p className="text-xs text-brand-dark/50 mb-5 leading-relaxed">
          {subtitle}
        </p>
      )}
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-brand-dark/70 mb-2">
        {label}
        {required && <span className="text-brand-gold ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-brand-dark/40 mt-1.5">{hint}</p>}
    </div>
  );
}

function SourceTab({
  active,
  onClick,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  hint: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3 py-3 text-sm rounded-md border transition-colors text-left",
        active
          ? "bg-brand-dark text-brand-cream border-brand-dark"
          : "bg-white text-brand-dark/70 border-brand-gray/30 hover:border-brand-gold",
      )}
    >
      <div className="font-medium">{label}</div>
      <div
        className={cn(
          "text-[11px] mt-0.5",
          active ? "text-brand-cream/70" : "text-brand-dark/40",
        )}
      >
        {hint}
      </div>
    </button>
  );
}

function EngineCard({
  active,
  onClick,
  label,
  price,
  desc,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  price: string;
  desc: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "text-left p-4 rounded-md border transition-colors min-w-0",
        active
          ? "bg-brand-dark text-brand-cream border-brand-dark"
          : "bg-white text-brand-dark/70 border-brand-gray/30 hover:border-brand-gold",
      )}
    >
      <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
        <span className="font-medium text-sm">{label}</span>
        <span
          className={cn(
            "text-[11px] num",
            active ? "text-brand-gold" : "text-brand-dark/40",
          )}
        >
          {price}
        </span>
      </div>
      <p
        className={cn(
          "text-[11px] leading-relaxed",
          active ? "text-brand-cream/70" : "text-brand-dark/45",
        )}
      >
        {desc}
      </p>
    </button>
  );
}

// === References 子元件 ===

interface RefDraft {
  ref_type: "web" | "text" | "file" | "image" | "pdf";
  title: string;
  description: string;
  url?: string;
  content?: string;
  file?: File;
}

function ReferenceList({
  refs,
  onChange,
}: {
  refs: RefDraft[];
  onChange: (next: RefDraft[]) => void;
}) {
  const addUrl = () => {
    onChange([
      ...refs,
      { ref_type: "web", title: "", description: "", url: "" },
    ]);
  };
  const addText = () => {
    onChange([
      ...refs,
      { ref_type: "text", title: "", description: "", content: "" },
    ]);
  };
  const addFile = (file: File) => {
    const type = file.type.startsWith("image/")
      ? "image"
      : file.type === "application/pdf"
        ? "pdf"
        : "file";
    onChange([
      ...refs,
      {
        ref_type: type,
        title: file.name,
        description: "",
        file,
      },
    ]);
  };

  const update = (idx: number, patch: Partial<RefDraft>) => {
    onChange(refs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const remove = (idx: number) => {
    onChange(refs.filter((_, i) => i !== idx));
  };

  return (
    <div>
      {refs.length === 0 && (
        <div className="text-xs text-brand-dark/40 italic mb-4">
          沒有參考資料就跳過這一段，等整理完隨時可以再加
        </div>
      )}

      {refs.map((r, idx) => (
        <div
          key={idx}
          className="border border-brand-gray/20 rounded-md p-4 mb-3 bg-brand-cream/40"
        >
          <div className="flex justify-between items-start mb-2">
            <span className="text-[10px] font-bold text-brand-gold uppercase tracking-[0.15em]">
              {r.ref_type}
            </span>
            <button
              onClick={() => remove(idx)}
              className="text-xs text-brand-dark/40 hover:text-red-700"
            >
              移除
            </button>
          </div>
          <input
            type="text"
            placeholder="標題"
            value={r.title}
            onChange={(e) => update(idx, { title: e.target.value })}
            className="mb-2"
          />
          <input
            type="text"
            placeholder="一兩句說明（選填）"
            value={r.description}
            onChange={(e) => update(idx, { description: e.target.value })}
            className="mb-2"
          />
          {r.ref_type === "web" && (
            <input
              type="url"
              placeholder="https://..."
              value={r.url}
              onChange={(e) => update(idx, { url: e.target.value })}
            />
          )}
          {r.ref_type === "text" && (
            <textarea
              placeholder="貼上內容文字"
              rows={4}
              value={r.content}
              onChange={(e) => update(idx, { content: e.target.value })}
            />
          )}
          {(r.ref_type === "file" ||
            r.ref_type === "image" ||
            r.ref_type === "pdf") && (
            <div className="text-xs text-brand-dark/60 num">
              {r.file?.name} (
              {((r.file?.size ?? 0) / 1024 / 1024).toFixed(2)} MB)
            </div>
          )}
        </div>
      ))}

      <div className="flex gap-2 mt-2 flex-wrap">
        <button
          onClick={addUrl}
          className="text-xs px-3 py-1.5 border border-dashed border-brand-gray/40 rounded-md hover:bg-brand-cream text-brand-dark/60"
        >
          + 加網頁連結
        </button>
        <button
          onClick={addText}
          className="text-xs px-3 py-1.5 border border-dashed border-brand-gray/40 rounded-md hover:bg-brand-cream text-brand-dark/60"
        >
          + 加文字
        </button>
        <label className="text-xs px-3 py-1.5 border border-dashed border-brand-gray/40 rounded-md hover:bg-brand-cream text-brand-dark/60 cursor-pointer">
          + 加檔案 / 圖片
          <input
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) addFile(f);
              e.target.value = "";
            }}
          />
        </label>
      </div>
    </div>
  );
}

async function uploadReference(meetingId: string, r: RefDraft) {
  if (r.ref_type === "web") {
    await api.addReference(meetingId, {
      ref_type: "web",
      title: r.title,
      description: r.description,
      url: r.url,
    });
    return;
  }
  if (r.ref_type === "text") {
    await fetch(`/api/meetings/${meetingId}/references`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ref_type: "text",
        title: r.title,
        description: r.description,
        content: r.content,
      }),
    });
    return;
  }
  if (r.file) {
    const form = new FormData();
    form.append("file", r.file);
    form.append("ref_type", r.ref_type);
    form.append("title", r.title);
    form.append("description", r.description);
    await fetch(`/api/meetings/${meetingId}/references`, {
      method: "POST",
      body: form,
    });
  }
}
